/**
 * Collection Configurator JavaScript
 * Handles popup, view toggle, filters, and add-to-cart functionality
 */

console.log('[Configurator] Script loaded');

(function() {
  'use strict';

  console.log('[Configurator] IIFE executing');

  // Storage keys
  const STORAGE_KEYS = {
    firstVisit: 'configurator_first_visit',
    viewPreference: 'configurator_view',
    lastSelection: 'configurator_selection'
  };

  /**
   * Normalize a value for use in file names
   * Converts to lowercase, replaces spaces with hyphens, removes brackets
   */
  function normalizeForFilename(value) {
    if (!value) return '';
    return value.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[()]/g, '');
  }

  function normalizeLabelForFilename(value) {
    if (!value) return '';
    return value
      .replace(/\s+/g, '-')
      .replace(/[()]/g, '');
  }

  // Cache for Storefront API variant data (handle → variants array)
  const variantCache = {};

  /**
   * Format a price amount using the shop's money format.
   * @param {string|number} amount - Price in main currency unit (e.g., "136.00")
   * @returns {string} Formatted price string (e.g., "136,00 €")
   */
  function formatMoney(amount) {
    const moneyFormat = window.configuratorMoneyFormat || '{{amount_with_comma_separator}} \u20ac';
    const cents = Math.round(parseFloat(amount) * 100);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormat.match(placeholderRegex);
    const formatType = match ? match[1] : 'amount_with_comma_separator';
    let value;

    function addCommas(n) { return n.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,'); }
    function addDots(n) { return n.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.'); }

    switch (formatType) {
      case 'amount':
        value = addCommas((cents / 100).toFixed(2));
        break;
      case 'amount_no_decimals':
        value = addCommas(Math.round(cents / 100).toString());
        break;
      case 'amount_with_comma_separator':
        value = addDots((cents / 100).toFixed(2).replace('.', ','));
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = addDots(Math.round(cents / 100).toString());
        break;
      default:
        value = addDots((cents / 100).toFixed(2).replace('.', ','));
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  // DOM Elements
  let wrapper, popup, productGrid;

  /**
   * Initialize the configurator
   */
  function init() {
    wrapper = document.getElementById('collection-configurator');
    if (!wrapper) {
      console.log('[Configurator] No wrapper found, skipping init');
      return;
    }

    console.log('[Configurator] Initializing...');

    // Fix for sticky sidebar: override #root overflow on desktop
    // This is needed because the theme sets overflow:hidden which breaks position:sticky
    if (window.innerWidth >= 769) {
      var rootEl = document.getElementById('root');
      if (rootEl) {
        rootEl.style.setProperty('overflow', 'visible', 'important');
        console.log('[Configurator] Set #root overflow to visible for sticky sidebar');
      }
    }

    popup = document.getElementById('configurator-popup');
    productGrid = document.getElementById('product-grid');

    initPopup();
    initViewToggle();
    initFilters();
    initAddToCart();
    initVariantSelectors();
    initProductLinkFix();

    console.log('[Configurator] Initialization complete');
  }

  // ==========================================================================
  // PRODUCT LINK FIX (2026-06-18)
  // Ensures product title/image links navigate properly even if another
  // script calls preventDefault. Uses capture phase to run first.
  // To revert: remove this function and the initProductLinkFix() call above.
  // ==========================================================================
  function initProductLinkFix() {
    if (!wrapper) return;
    wrapper.addEventListener('click', function(e) {
      var link = e.target.closest('.card-title a, .card-image-link, .list-title a, .list-image-link');
      if (link && link.href && !e.target.closest('button, select, .bv_mainselect')) {
        e.stopImmediatePropagation();
        window.location.href = link.href;
      }
    }, true);
    console.log('[Configurator] Product link fix initialized');
  }

  // ==========================================================================
  // POPUP FUNCTIONALITY
  // ==========================================================================

  function initPopup() {
    if (!popup) return;

    if (popup.parentNode !== document.body) {
      document.body.appendChild(popup);
    }

    const showOnFirstVisit = wrapper.dataset.showPopupFirstVisit === 'true';
    const hasSelection = wrapper.dataset.hasSelection === 'true';

    // Show popup on first visit if enabled and no selection made yet
    if (showOnFirstVisit && !hasSelection && !hasVisitedBefore()) {
      setTimeout(() => openPopup(), 500);
      markAsVisited();
    }

    // Open popup button
    const openButtons = wrapper.querySelectorAll('[data-open-configurator]');
    openButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openPopup();
      });
    });

    // Close popup buttons
    const closeButtons = popup.querySelectorAll('[data-close-popup]');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        closePopup();
      });
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('shown')) {
        closePopup();
      }
    });

    // Style and color selection
    initOptionSelection();

    // Apply button
    const applyButton = document.getElementById('configurator-apply');
    if (applyButton) {
      applyButton.addEventListener('click', applyConfiguration);
    }
  }

  function hasVisitedBefore() {
    try {
      return localStorage.getItem(STORAGE_KEYS.firstVisit) === 'true';
    } catch (e) {
      return false;
    }
  }

  function markAsVisited() {
    try {
      localStorage.setItem(STORAGE_KEYS.firstVisit, 'true');
    } catch (e) {
      // localStorage not available
    }
  }

  function openPopup() {
    if (!popup) return;
    popup.classList.add('shown');
    document.body.style.overflow = 'hidden';

    // Focus first interactive element
    const firstInput = popup.querySelector('input[type="radio"]');
    if (firstInput) {
      firstInput.focus();
    }
  }

  function closePopup() {
    if (!popup) return;
    popup.classList.remove('shown');
    document.body.style.overflow = '';
  }

  function initOptionSelection() {
    const styleInputs = popup.querySelectorAll('input[name="configurator-style"]');
    const surfaceInputs = popup.querySelectorAll('input[name="configurator-surface"]');

    // Parse valid combinations
    let validCombinations = {};
    try {
      const combinationsData = popup.dataset.validCombinations;
      if (combinationsData) {
        validCombinations = JSON.parse(combinationsData);
      }
    } catch (e) {
      console.warn('Could not parse valid combinations:', e);
    }

    styleInputs.forEach(input => {
      input.addEventListener('change', () => {
        updateActiveState(input, '.style-button');
        filterSurfaceOptions(input.dataset.normalized || normalizeForFilename(input.value), validCombinations);
        updatePreviewImage();
      });
    });

    surfaceInputs.forEach(input => {
      input.addEventListener('change', () => {
        updateActiveState(input, '.surface-button');
        updatePreviewImage();
      });
    });

    // Initial filter based on current style selection
    const checkedStyle = popup.querySelector('input[name="configurator-style"]:checked');
    if (checkedStyle && Object.keys(validCombinations).length > 0) {
      filterSurfaceOptions(checkedStyle.dataset.normalized || normalizeForFilename(checkedStyle.value), validCombinations);
    }
  }

  function filterSurfaceOptions(styleNormalized, validCombinations) {
    const surfaceButtons = popup.querySelectorAll('.surface-button');
    const validSurfaces = validCombinations[styleNormalized] || [];

    console.log('[Configurator] Filtering surfaces for style:', styleNormalized);
    console.log('[Configurator] Valid surfaces:', validSurfaces);
    console.log('[Configurator] All combinations:', validCombinations);

    let selectionCleared = false;

    surfaceButtons.forEach(button => {
      const input = button.querySelector('input');
      const surfaceNormalized = input.dataset.normalized || normalizeForFilename(input.value);

      console.log('[Configurator] Checking surface:', surfaceNormalized, 'isValid:', validSurfaces.includes(surfaceNormalized));

      if (validSurfaces.includes(surfaceNormalized)) {
        button.style.setProperty('display', 'flex', 'important');
        button.classList.remove('unavailable');
      } else {
        button.style.setProperty('display', 'none', 'important');
        button.classList.add('unavailable');
        if (input.checked) {
          input.checked = false;
          button.classList.remove('active');
          selectionCleared = true;
        }
      }
    });

    if (selectionCleared) {
      updatePreviewImage();
    }
  }

  function updateActiveState(input, parentSelector) {
    const allButtons = popup.querySelectorAll(parentSelector);
    allButtons.forEach(btn => btn.classList.remove('active'));
    input.closest(parentSelector).classList.add('active');
  }

  function updatePreviewImage() {
    const styleInput = popup.querySelector('input[name="configurator-style"]:checked');
    const surfaceInput = popup.querySelector('input[name="configurator-surface"]:checked');
    const previewImg = document.getElementById('configurator-preview-image');
    const previewWrapper = popup.querySelector('.configurator-preview');
    const previewCaption = popup.querySelector('.preview-caption');
    const placeholder = popup.querySelector('.preview-placeholder');
    const loadingIndicator = popup.querySelector('.preview-loading');

    if (!styleInput || !surfaceInput || !previewImg) {
      if (previewWrapper) {
        previewWrapper.classList.add('is-empty');
      }
      if (placeholder) {
        placeholder.textContent = placeholder.dataset.emptyText || 'Bitte wählen Sie Stil und Oberfläche aus.';
      }
      if (loadingIndicator) {
        loadingIndicator.classList.remove('active');
      }
      if (previewCaption) {
        const styleSpan = previewCaption.querySelector('.preview-style');
        const surfaceSpan = previewCaption.querySelector('.preview-surface');
        if (styleSpan) styleSpan.textContent = '';
        if (surfaceSpan) surfaceSpan.textContent = '';
      }
      return;
    }

    if (previewWrapper) {
      previewWrapper.classList.remove('is-empty');
    }

    // Get the label values (capitalized) for the filename
    const styleLabel = styleInput.dataset.label || styleInput.value;
    const surfaceLabel = surfaceInput.dataset.label || surfaceInput.value;

    // Build filename: Style-Surface.jpg (e.g., "Standard-Matt.jpg")
    const styleFilename = normalizeLabelForFilename(styleLabel);
    const surfaceFilename = normalizeLabelForFilename(surfaceLabel);
    const defaultImageName = styleFilename + '-' + surfaceFilename + '.jpg';
    const combinationKey = normalizeForFilename(styleInput.value) + '--' + normalizeForFilename(surfaceInput.value);
    let previewImageMap = {};
    try {
      previewImageMap = JSON.parse(popup.dataset.previewImageMap || '{}');
    } catch (e) {
      console.warn('[Configurator] Could not parse preview image map:', e);
    }
    const mappedImageUrl = previewImageMap[combinationKey];
    const imageName = mappedImageUrl ? mappedImageUrl.split('/').pop().split('?')[0] : defaultImageName;

    console.log('[Configurator] Building preview image:', imageName);

    // Show loading state
    if (loadingIndicator) {
      loadingIndicator.classList.add('active');
    }

    // Get the Shopify Files CDN base URL from current image
    const currentSrc = previewImg.src;
    let newSrc = mappedImageUrl;

    if (!newSrc && currentSrc.includes('cdn.shopify.com/s/files')) {
      // Extract the base URL up to /files/ and append the new filename
      const filesIndex = currentSrc.indexOf('/files/');
      if (filesIndex !== -1) {
        // Find the next slash after /files/1/xxxx/xxxx/files/
        const pathAfterFiles = currentSrc.substring(filesIndex + 7); // after '/files/'
        const parts = pathAfterFiles.split('/');
        // Rebuild: /files/{shop_id_part1}/{shop_id_part2}/files/{filename}
        if (parts.length >= 3) {
          const baseUrl = currentSrc.substring(0, currentSrc.lastIndexOf('/') + 1);
          newSrc = baseUrl + imageName;
        }
      }
    }

    // Fallback: replace just the filename
    if (!newSrc) {
      newSrc = currentSrc.replace(/[^\/]+\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, imageName);
    }

    console.log('[Configurator] New image URL:', newSrc);

    // Load new image
    const tempImg = new Image();
    tempImg.onload = function() {
      previewImg.src = newSrc;
      if (loadingIndicator) {
        loadingIndicator.classList.remove('active');
      }
      console.log('[Configurator] Preview image loaded successfully');
    };
    tempImg.onerror = function() {
      // If image fails to load, keep current image but hide loading
      if (loadingIndicator) {
        loadingIndicator.classList.remove('active');
      }
      if (previewWrapper) {
        previewWrapper.classList.add('is-empty');
      }
      if (placeholder) {
        let missingPath = '';
        if (newSrc) {
          try {
            const parsedUrl = new URL(newSrc, window.location.origin);
            missingPath = parsedUrl.pathname || '';
          } catch (e) {
            missingPath = newSrc.split('?')[0];
          }
        }
        const missingText = placeholder.dataset.missingText || 'Vorschaubild nicht verfügbar.';
        placeholder.textContent = missingPath ? `${missingText}\n${missingPath}` : missingText;
      }
      console.warn('[Configurator] Preview image not found:', imageName, 'URL:', newSrc);
    };
    tempImg.src = newSrc;

    // Update caption
    if (previewCaption) {
      const styleSpan = previewCaption.querySelector('.preview-style');
      const surfaceSpan = previewCaption.querySelector('.preview-surface');
      if (styleSpan) styleSpan.textContent = styleLabel;
      if (surfaceSpan) surfaceSpan.textContent = surfaceLabel;
    }
  }

  function applyConfiguration() {
    const styleInput = popup.querySelector('input[name="configurator-style"]:checked');
    const surfaceInput = popup.querySelector('input[name="configurator-surface"]:checked');

    if (!styleInput || !surfaceInput) {
      alert('Bitte wählen Sie Stil und Oberfläche aus.');
      return;
    }

    const style = styleInput.value;
    const surface = surfaceInput.value;
    const collectionUrl = popup.dataset.collectionUrl || wrapper.dataset.collectionUrl;

    // Save selection
    try {
      localStorage.setItem(STORAGE_KEYS.lastSelection, JSON.stringify({ style, surface }));
    } catch (e) {
      // localStorage not available
    }

    // Build redirect URL with Shopify metafield filter params
    const url = new URL(collectionUrl, window.location.origin);
    url.searchParams.set('view', 'configurator');
    // Use Shopify's metafield filter URL format
    url.searchParams.set('filter.p.m.custom.filter_style', style);
    url.searchParams.set('filter.p.m.custom.filter_surface', surface);

    // Redirect to filtered collection
    window.location.href = url.toString();
  }

  // ==========================================================================
  // VIEW TOGGLE (Grid/List)
  // ==========================================================================

  function initViewToggle() {
    const viewButtons = wrapper.querySelectorAll('[data-view]');

    // Load saved preference or use mobile default
    let savedView = getSavedViewPreference();

    // On mobile (< 768px), default to list view if no saved preference
    if (!savedView && window.innerWidth < 768) {
      savedView = 'list';
    }

    if (savedView && productGrid) {
      setView(savedView);
      updateViewButtons(savedView);
    }

    viewButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const view = btn.dataset.view;
        setView(view);
        updateViewButtons(view);
        saveViewPreference(view);
      });
    });
  }

  function setView(view) {
    if (!productGrid) return;
    productGrid.dataset.view = view;
  }

  function updateViewButtons(activeView) {
    const viewButtons = wrapper.querySelectorAll('[data-view]');
    viewButtons.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.view === activeView);
    });
  }

  function getSavedViewPreference() {
    try {
      return localStorage.getItem(STORAGE_KEYS.viewPreference);
    } catch (e) {
      return null;
    }
  }

  function saveViewPreference(view) {
    try {
      localStorage.setItem(STORAGE_KEYS.viewPreference, view);
    } catch (e) {
      // localStorage not available
    }
  }

  // ==========================================================================
  // FILTERS
  // ==========================================================================

  function initFilters() {
    const filterForm = document.getElementById('configurator-filter-form');
    const filterToggle = wrapper.querySelector('[data-toggle-filters]');
    const filterSidebar = document.getElementById('configurator-filters');
    const closeFiltersBtn = wrapper.querySelector('[data-close-filters]');
    const filterSidebarParent = filterSidebar ? filterSidebar.parentNode : null;
    const filterSidebarNextSibling = filterSidebar ? filterSidebar.nextSibling : null;
    let filterSidebarMoved = false;

    if (!filterForm) return;

    // Filter form submission (fallback)
    filterForm.addEventListener('submit', (e) => {
      e.preventDefault();
      applyFilters(filterForm);
    });

    // Auto-apply filters on any input change
    const checkboxes = filterForm.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(cb => {
      cb.addEventListener('change', () => applyFilters(filterForm));
    });

    // Auto-apply on price range change (with debounce)
    const priceInputs = filterForm.querySelectorAll('input[type="number"]');
    let priceTimeout;
    priceInputs.forEach(input => {
      input.addEventListener('change', () => {
        clearTimeout(priceTimeout);
        priceTimeout = setTimeout(() => applyFilters(filterForm), 500);
      });
    });

    // Mobile filter toggle
    if (filterToggle && filterSidebar) {
      filterToggle.addEventListener('click', (e) => {
        e.preventDefault();
        moveFilterSidebarToBody();
        filterSidebar.classList.add('open');
        document.body.style.overflow = 'hidden';

        // Add overlay
        let overlay = document.querySelector('.cfg-filter-overlay');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'cfg-filter-overlay';
          document.body.appendChild(overlay);
        }
        overlay.classList.add('active');
        overlay.addEventListener('click', closeFilters);
      });

      if (closeFiltersBtn) {
        closeFiltersBtn.addEventListener('click', closeFilters);
      }
    }

    function moveFilterSidebarToBody() {
      if (!filterSidebar || filterSidebarMoved) return;
      if (!window.matchMedia('(max-width: 768px)').matches) return;
      if (filterSidebarParent && filterSidebarParent !== document.body) {
        document.body.appendChild(filterSidebar);
        filterSidebarMoved = true;
      }
    }

    function closeFilters() {
      filterSidebar.classList.remove('open');
      document.body.style.overflow = '';
      const overlay = document.querySelector('.cfg-filter-overlay');
      if (overlay) overlay.classList.remove('active');
      restoreFilterSidebar();
    }

    function restoreFilterSidebar() {
      if (!filterSidebar || !filterSidebarMoved || !filterSidebarParent) return;
      if (filterSidebarNextSibling && filterSidebarNextSibling.parentNode === filterSidebarParent) {
        filterSidebarParent.insertBefore(filterSidebar, filterSidebarNextSibling);
      } else {
        filterSidebarParent.appendChild(filterSidebar);
      }
      filterSidebarMoved = false;
    }
  }

  function applyFilters(form) {
    const formData = new FormData(form);
    const currentUrl = new URL(window.location.href);

    // Preserve view, style and color params
    const style = currentUrl.searchParams.get('style');
    const color = currentUrl.searchParams.get('color');

    // Build new URL with filter params
    const newUrl = new URL(currentUrl.pathname, window.location.origin);

    // Always keep view=configurator
    newUrl.searchParams.set('view', 'configurator');
    if (style) newUrl.searchParams.set('style', style);
    if (color) newUrl.searchParams.set('color', color);

    // Add filter params
    for (const [key, value] of formData.entries()) {
      if (value) {
        newUrl.searchParams.append(key, value);
      }
    }

    window.location.href = newUrl.toString();
  }

  // ==========================================================================
  // ADD TO CART
  // ==========================================================================

  function initAddToCart() {
    const forms = wrapper.querySelectorAll('.card-form, .list-form');
    console.log('[Configurator] Found', forms.length, 'forms for ATC');

    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        console.log('[Configurator] Form submitted');
        addToCart(form);
      });
    });

    // Also handle direct button clicks as backup
    const addButtons = wrapper.querySelectorAll('.add-to-cart-btn');
    console.log('[Configurator] Found', addButtons.length, 'ATC buttons');

    addButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('[Configurator] Button clicked');
        const form = btn.closest('form');
        if (form) {
          addToCart(form);
        }
      });
    });
  }

  function addToCart(form) {
    console.log('[Configurator] addToCart called');

    const submitBtn = form.querySelector('button[type="submit"]');
    if (!submitBtn) {
      console.error('[Configurator] No submit button found');
      return;
    }
    if (submitBtn.classList.contains('disabled') || submitBtn.classList.contains('processing')) {
      console.log('[Configurator] Button disabled or processing');
      return;
    }

    // Resolve variant from current dropdown state (single source of truth)
    const resolved = resolveVariantFromDropdowns(form);
    let variantId = resolved ? resolved.variantId : null;
    console.log('[Configurator] Resolved variant:', variantId, resolved?.variantOption?.textContent?.trim());

    // Last resort fallback
    if (!variantId) {
      const hiddenInput = form.querySelector('input[name="id"]');
      variantId = hiddenInput ? hiddenInput.value : null;
      console.log('[Configurator] Fallback to hidden input:', variantId);
    }

    if (!variantId) {
      console.error('[Configurator] No variant ID found');
      return;
    }

    // Add loading state
    submitBtn.classList.add('processing');
    const originalText = submitBtn.innerHTML;
    submitBtn.innerHTML = '<span class="spinner"></span>';

    // Build form data with just the variant ID
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    const rootUrl = window.Shopify?.routes?.root || '/';
    const fetchUrl = rootUrl + 'cart/add.js';
    console.log('[Configurator] Fetching:', fetchUrl, 'with id:', variantId);

    fetch(fetchUrl, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      console.log('[Configurator] Response status:', response.status);
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.description || 'Fehler beim Hinzufügen');
        });
      }
      return response.json();
    })
    .then(data => {
      // Success - update cart
      console.log('[Configurator] Success:', data);
      submitBtn.innerHTML = '<i class="icon-check" aria-hidden="true"></i> Hinzugefügt';

      // Update cart count if element exists
      updateCartCount();

      // Open cart drawer if available (uses existing theme functionality)
      if (typeof ajaxCart !== 'undefined' && ajaxCart.load) {
        ajaxCart.load();
      }

      // Try to trigger theme's cart update mechanisms
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      }

      // Dispatch Shopify section rendering event
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));

      // Trigger custom events for other scripts
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));

      // Reset button after delay
      setTimeout(() => {
        submitBtn.innerHTML = originalText;
        submitBtn.classList.remove('processing');
      }, 1500);
    })
    .catch(error => {
      console.error('Add to cart error:', error);
      submitBtn.innerHTML = originalText;
      submitBtn.classList.remove('processing');

      // Show error message
      showNotification(error.message || 'Fehler beim Hinzufügen zum Warenkorb', 'error');
    });
  }

  function updateCartCount() {
    const rootUrl = window.Shopify?.routes?.root || '/';
    fetch(rootUrl + 'cart.js', {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(cart => {
      const countElements = document.querySelectorAll('#cart-count, .cart-count, [data-cart-count]');
      countElements.forEach(el => {
        el.textContent = cart.item_count;
      });
    })
    .catch(error => {
      console.error('Error updating cart count:', error);
    });
  }

  function showNotification(message, type = 'info') {
    // Use existing theme notification system if available
    if (typeof window.showAlert === 'function') {
      window.showAlert({ message, type });
      return;
    }

    // Dispatch custom event for theme's alert system
    const alertEvent = new CustomEvent('showAlert', {
      detail: { message, type }
    });
    window.dispatchEvent(alertEvent);

    // Fallback: simple alert
    if (type === 'error') {
      console.error(message);
    }
  }

  // ==========================================================================
  // VARIANT SELECTORS (Multi-Option Support)
  // ==========================================================================

  function initVariantSelectors() {
    // Listen for changes on individual option dropdowns
    const optionSelects = wrapper.querySelectorAll('.option-select');
    const gridSelects = Array.from(optionSelects).filter(s => s.id.startsWith('option-'));
    const listSelects = Array.from(optionSelects).filter(s => s.id.startsWith('list-option-'));

    console.log('[Configurator] Found', optionSelects.length, 'option selects for variant binding');
    console.log('[Configurator] Grid view selects:', gridSelects.length, 'List view selects:', listSelects.length);

    optionSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        const isListSelect = e.target.id.startsWith('list-option-');
        console.log('[Configurator] Option select changed:', e.target.id, 'value:', e.target.value, 'isList:', isListSelect);
        handleOptionChange(e.target);
      });
    });

    // On init, filter each product's option dropdowns to only show available values
    // Skip products with >= 250 variants — they'll be filtered after API fetch completes
    const forms = wrapper.querySelectorAll('.card-form, .list-form');
    forms.forEach(form => {
      const card = form.closest('.product-card');
      const totalVariants = card ? parseInt(card.dataset.totalVariants, 10) : 0;
      if (isNaN(totalVariants) || totalVariants < 250) {
        updateAvailableOptions(form);

        // Sync initial price to match the default selected variant
        const masterSelect = form.querySelector('.variant-select-master');
        const optionSelects = form.querySelectorAll('.option-select');
        if (masterSelect && optionSelects.length > 0) {
          const selectedOptions = [];
          optionSelects.forEach(function(sel, idx) { selectedOptions[idx] = sel.value; });
          const match = findMatchingVariant(masterSelect, selectedOptions);
          if (match) {
            masterSelect.value = match.value;
            updateVariantUI(card, form, match);
          }
        }
      }
    });

    // Fetch full variant data for products with >=250 variants via Storefront API
    // Try: 1) theme setting (if valid), 2) Shopify's built-in embedded token, 3) meta tag
    // Note: Admin API tokens (shpat_) are filtered out — only Storefront tokens work
    const configuredToken = wrapper.dataset.storefrontToken;
    const storefrontToken = (configuredToken && !configuredToken.startsWith('shpat_') ? configuredToken : null)
      || (function() {
        // Shopify embeds an accessToken in inline scripts for dynamic checkout etc.
        var scripts = document.querySelectorAll('script:not([src])');
        for (var s = 0; s < scripts.length; s++) {
          var m = scripts[s].textContent.match(/"accessToken"\s*:\s*"([a-f0-9]{32})"/);
          if (m) return m[1];
        }
        return null;
      })()
      || (document.querySelector('meta[name="shopify-storefront-access-token"]') || {}).content
      || null;

    if (storefrontToken) {
      console.log('[Configurator] Storefront API token found, length:', storefrontToken.length);
      fetchTruncatedVariants(storefrontToken);
    } else {
      // Check if any products are truncated and warn
      const truncated = wrapper.querySelectorAll('.product-card[data-total-variants]');
      let hasTruncated = false;
      truncated.forEach(card => {
        if (parseInt(card.dataset.totalVariants, 10) >= 250) hasTruncated = true;
      });
      if (hasTruncated) {
        console.warn('[Configurator] Products with >=250 variants detected but no Storefront API token found. Some variant combinations may be unavailable.');
      }
    }
  }

  /**
   * Cascading option filter: hide/disable option values that have no matching
   * variant in the master select given the current selections of preceding options.
   * This prevents users from selecting combinations that don't exist.
   */
  function updateAvailableOptions(form) {
    const masterSelect = form.querySelector('.variant-select-master');
    const optionSelects = form.querySelectorAll('.option-select');
    if (!masterSelect || optionSelects.length === 0) return;

    const allVariants = masterSelect.querySelectorAll('option');

    // Helper: get trimmed option values [o1, o2, o3] from a variant element
    function getVarVals(v) {
      return [
        (v.dataset.option1 || '').trim(),
        (v.dataset.option2 || '').trim(),
        (v.dataset.option3 || '').trim()
      ];
    }

    // Collect current selected values (may be corrected during this loop)
    const selected = [];
    optionSelects.forEach((sel, i) => { selected[i] = sel.value.trim(); });

    // For each option position, determine available values given preceding selections
    for (let optIdx = 0; optIdx < optionSelects.length; optIdx++) {
      const available = new Set();

      for (const v of allVariants) {
        const vv = getVarVals(v);
        // Check that all preceding options match current selections
        let ok = true;
        for (let j = 0; j < optIdx; j++) {
          if (selected[j] !== vv[j]) { ok = false; break; }
        }
        if (ok) available.add(vv[optIdx]);
      }

      // Update dropdown: hide unavailable, show available
      const sel = optionSelects[optIdx];
      let currentValid = available.has(selected[optIdx]);

      for (const opt of sel.options) {
        const isAvail = available.has(opt.value.trim());
        opt.disabled = !isAvail;
        opt.style.display = isAvail ? '' : 'none';
      }

      // If current selection is unavailable, pick the first available value
      if (!currentValid) {
        for (const opt of sel.options) {
          if (!opt.disabled) {
            sel.value = opt.value;
            selected[optIdx] = opt.value.trim();
            break;
          }
        }
      }
    }
  }

  // ==========================================================================
  // STOREFRONT API – Fetch all variants for products with >250 variants
  // ==========================================================================

  /**
   * Fetch ALL variants for a product via the Storefront API GraphQL endpoint.
   * Paginates automatically (250 per page) until all variants are retrieved.
   * @param {string} handle - Product handle
   * @param {string} token - Storefront API access token
   * @returns {Promise<Array>} Array of variant objects
   */
  async function fetchAllVariants(handle, token) {
    const shopDomain = window.location.hostname;
    const apiUrl = 'https://' + shopDomain + '/api/2024-10/graphql.json';
    const allVariants = [];
    let hasNextPage = true;
    let cursor = null;

    while (hasNextPage) {
      const afterClause = cursor ? ', after: "' + cursor + '"' : '';
      const query = [
        '{ product(handle: "' + handle + '") {',
        '    variants(first: 250' + afterClause + ') {',
        '      edges {',
        '        node {',
        '          id title',
        '          price { amount currencyCode }',
        '          compareAtPrice { amount currencyCode }',
        '          availableForSale',
        '          selectedOptions { name value }',
        '          image { url }',
        '        }',
        '        cursor',
        '      }',
        '      pageInfo { hasNextPage }',
        '    }',
        '  }',
        '}'
      ].join(' ');

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query: query })
      });

      if (!response.ok) {
        throw new Error('Storefront API error: ' + response.status);
      }

      const data = await response.json();
      if (data.errors) {
        throw new Error(data.errors[0].message);
      }

      const product = data.data && data.data.product;
      if (!product) {
        throw new Error('Product not found: ' + handle);
      }

      const edges = product.variants.edges;
      for (let i = 0; i < edges.length; i++) {
        allVariants.push(edges[i].node);
      }

      hasNextPage = product.variants.pageInfo.hasNextPage;
      if (hasNextPage && edges.length > 0) {
        cursor = edges[edges.length - 1].cursor;
      }
    }

    return allVariants;
  }

  /**
   * Replace the master select options in both grid and list forms of a product
   * card with the full variant data fetched from the Storefront API.
   * @param {HTMLElement} card - The .product-card element
   * @param {Array} variants - Array of variant objects from GraphQL
   */
  function populateMasterSelects(card, variants) {
    const masterSelects = card.querySelectorAll('.variant-select-master');

    masterSelects.forEach(masterSelect => {
      const currentValue = masterSelect.value;
      const productUrl = masterSelect.dataset.productUrl || '';

      // Clear and rebuild with full variant data
      masterSelect.innerHTML = '';

      variants.forEach(v => {
        // Extract numeric ID from gid://shopify/ProductVariant/12345
        const numericId = v.id.replace('gid://shopify/ProductVariant/', '');

        const opt = document.createElement('option');
        opt.value = numericId;
        opt.textContent = v.title;

        // Map selectedOptions to data-option1/2/3
        v.selectedOptions.forEach((so, idx) => {
          if (idx < 3) opt.dataset['option' + (idx + 1)] = so.value;
        });
        // Fill remaining option slots with empty string
        for (let k = v.selectedOptions.length; k < 3; k++) {
          opt.dataset['option' + (k + 1)] = '';
        }

        opt.dataset.price = formatMoney(v.price.amount);

        if (v.compareAtPrice && parseFloat(v.compareAtPrice.amount) > parseFloat(v.price.amount)) {
          opt.dataset.comparePrice = formatMoney(v.compareAtPrice.amount);
        } else {
          opt.dataset.comparePrice = '';
        }

        opt.dataset.available = v.availableForSale ? 'true' : 'false';

        if (v.image && v.image.url) {
          opt.dataset.image = v.image.url;
        }

        // Restore previous selection
        if (numericId === currentValue) {
          opt.selected = true;
        }

        masterSelect.appendChild(opt);
      });

      // Restore product URL data attribute
      if (productUrl) {
        masterSelect.dataset.productUrl = productUrl;
      }

      console.log('[Configurator] Populated master select with', variants.length, 'variants (was', currentValue ? 'selected: ' + currentValue : 'none', ')');
    });
  }

  /**
   * Find all product cards with >250 variants and fetch their complete
   * variant data via the Storefront API. Processes in batches of 4.
   * @param {string} token - Storefront API access token
   */
  async function fetchTruncatedVariants(token) {
    const cards = wrapper.querySelectorAll('.product-card[data-total-variants]');
    const queue = [];

    cards.forEach(card => {
      const total = parseInt(card.dataset.totalVariants, 10);
      if (isNaN(total) || total < 250) return;

      const handle = card.dataset.productHandle;
      if (!handle) return;

      queue.push({ card, handle, total });
    });

    if (queue.length === 0) return;

    console.log('[Configurator] Found', queue.length, 'products with >250 variants, fetching full data via Storefront API...');

    // Process in batches of 4 to avoid rate limiting
    const BATCH_SIZE = 4;
    for (let i = 0; i < queue.length; i += BATCH_SIZE) {
      const batch = queue.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(async ({ card, handle, total }) => {
        try {
          const variants = await fetchAllVariants(handle, token);
          console.log('[Configurator] Fetched', variants.length, '/', total, 'variants for', handle);
          variantCache[handle] = variants;
          populateMasterSelects(card, variants);

          // Re-run cascading option filter with full data, then sync displayed prices
          card.querySelectorAll('.card-form, .list-form').forEach(form => {
            updateAvailableOptions(form);

            // Sync displayed price/UI with the current dropdown selection
            const masterSelect = form.querySelector('.variant-select-master');
            const optionSelects = form.querySelectorAll('.option-select');
            if (masterSelect && optionSelects.length > 0) {
              const selectedOptions = [];
              optionSelects.forEach((sel, idx) => { selectedOptions[idx] = sel.value; });
              const match = findMatchingVariant(masterSelect, selectedOptions);
              if (match) {
                masterSelect.value = match.value;
                updateVariantUI(card, form, match);
              }
            }
          });
        } catch (err) {
          console.error('[Configurator] Failed to fetch variants for', handle, ':', err.message);
        }
      }));
    }

    console.log('[Configurator] All variant data loaded');
  }

  /**
   * Handle change on individual option dropdown
   * Finds matching variant and updates master select + UI
   */
  function handleOptionChange(optionSelect) {
    const form = optionSelect.closest('form');
    const card = optionSelect.closest('.product-card');
    const formClass = form?.className || 'unknown';
    const formId = form?.getAttribute('id') || 'no-id';
    const isListView = formClass.includes('list-form');

    console.log('[Configurator] handleOptionChange - form class:', formClass, 'form id:', formId, 'isListView:', isListView, 'card:', card?.dataset.productId);

    if (!form || !card) {
      console.warn('[Configurator] No form or card found');
      return;
    }

    // Get the master variant selector for this form
    const masterSelect = form.querySelector('.variant-select-master');
    if (!masterSelect) {
      console.warn('[Configurator] No master select found in form');
      return;
    }

    console.log('[Configurator] Master select current value:', masterSelect.value);

    // Collect all selected option values from this form
    const optionSelects = form.querySelectorAll('.option-select');
    const selectedOptions = [];

    optionSelects.forEach((select, index) => {
      selectedOptions[index] = select.value;
      console.log('[Configurator] Reading option', index, 'id:', select.id, 'value:', select.value, 'selectedIndex:', select.selectedIndex);
    });

    console.log('[Configurator] Selected options array:', JSON.stringify(selectedOptions));

    // Determine which option the user just changed
    const changedIndex = parseInt(optionSelect.dataset.optionIndex, 10);

    // Find matching variant in master select (with smart fallback for invalid combos)
    const matchingVariant = findMatchingVariant(masterSelect, selectedOptions, isNaN(changedIndex) ? undefined : changedIndex);

    if (matchingVariant) {
      console.log('[Configurator] Found matching variant:', matchingVariant.value, matchingVariant.textContent.trim());
      // Update master select value
      masterSelect.value = matchingVariant.value;

      // Also explicitly set the selected attribute to ensure it persists
      Array.from(masterSelect.options).forEach(opt => {
        opt.selected = (opt.value === matchingVariant.value);
      });

      console.log('[Configurator] Master select updated to:', masterSelect.value);

      // Sync option dropdowns to the matched variant
      // (handles partial matches where the exact combo doesn't exist)
      const matchedValues = [
        (matchingVariant.dataset.option1 || '').trim(),
        (matchingVariant.dataset.option2 || '').trim(),
        (matchingVariant.dataset.option3 || '').trim()
      ];
      optionSelects.forEach((select, index) => {
        if (index < matchedValues.length && matchedValues[index] && select.value !== matchedValues[index]) {
          console.log('[Configurator] Correcting option', index, 'from', select.value, 'to', matchedValues[index]);
          select.value = matchedValues[index];
        }
      });

      // Update cascading option availability for this form
      // (do this BEFORE price update so options are finalized)
      updateAvailableOptions(form);

      // Re-read options after cascading filter may have changed them
      const finalOptions = [];
      optionSelects.forEach(function(sel, idx) { finalOptions[idx] = sel.value; });
      const finalMatch = findMatchingVariant(masterSelect, finalOptions);
      const resolvedVariant = finalMatch || matchingVariant;

      // Sync master select to final resolved variant
      if (finalMatch && finalMatch.value !== masterSelect.value) {
        masterSelect.value = finalMatch.value;
        Array.from(masterSelect.options).forEach(function(opt) {
          opt.selected = (opt.value === finalMatch.value);
        });
      }

      // Update UI based on resolved variant
      updateVariantUI(card, form, resolvedVariant);

      // Dispatch event so nf-projekt.js can stay in sync
      form.dispatchEvent(new CustomEvent('variant:resolved', {
        bubbles: true,
        detail: {
          variantId: resolvedVariant.value,
          variantOption: resolvedVariant
        }
      }));
    } else {
      console.warn('[Configurator] No matching variant found for options:', selectedOptions);
    }
  }

  /**
   * Resolve the current variant from dropdown values in a given form.
   * This is the single source of truth for "what variant is currently selected?"
   * Returns { variantId, variantOption } or null if no match.
   */
  function resolveVariantFromDropdowns(form) {
    const masterSelect = form.querySelector('.variant-select-master');
    const optionSelects = form.querySelectorAll('.option-select');

    if (!masterSelect) return null;

    // If no option selects, just return current master value
    if (optionSelects.length === 0) {
      const selectedOption = masterSelect.options[masterSelect.selectedIndex];
      return selectedOption ? {
        variantId: selectedOption.value,
        variantOption: selectedOption
      } : null;
    }

    // Read current dropdown values
    const selectedOptions = [];
    optionSelects.forEach((select, index) => {
      selectedOptions[index] = select.value;
    });

    // Find matching variant in master select
    const matchingVariant = findMatchingVariant(masterSelect, selectedOptions);

    if (matchingVariant) {
      // Sync master select to this value
      masterSelect.value = matchingVariant.value;
      Array.from(masterSelect.options).forEach(opt => {
        opt.selected = (opt.value === matchingVariant.value);
      });
      return {
        variantId: matchingVariant.value,
        variantOption: matchingVariant
      };
    }

    // Fallback to master select current value
    const fallback = masterSelect.options[masterSelect.selectedIndex];
    return fallback ? {
      variantId: fallback.value,
      variantOption: fallback
    } : null;
  }

  /**
   * Find variant option that matches all selected options
   */
  function findMatchingVariant(masterSelect, selectedOptions, changedIndex) {
    const allVariantOptions = masterSelect.querySelectorAll('option');
    console.log('[Configurator] Searching', allVariantOptions.length, 'variants for match');

    // Helper: get trimmed option values from a variant element
    function getVariantValues(option) {
      return [
        (option.dataset.option1 || '').trim(),
        (option.dataset.option2 || '').trim(),
        (option.dataset.option3 || '').trim()
      ];
    }

    // Helper: score a variant against selected options
    function scoreVariant(variantValues, selected, mustMatchIndex) {
      let score = 0;
      if (mustMatchIndex !== undefined && mustMatchIndex < selected.length) {
        if ((selected[mustMatchIndex] || '').trim() !== variantValues[mustMatchIndex]) {
          return -1; // Disqualified
        }
        score += 1000;
      }
      for (let i = 0; i < selected.length; i++) {
        if ((selected[i] || '').trim() === variantValues[i]) {
          score += Math.pow(10, selected.length - i);
        }
      }
      return score;
    }

    // Pass 1: try exact match
    for (const option of allVariantOptions) {
      const variantValues = getVariantValues(option);
      let matches = true;
      for (let i = 0; i < selectedOptions.length; i++) {
        if ((selectedOptions[i] || '').trim() !== variantValues[i]) {
          matches = false;
          break;
        }
      }
      if (matches) {
        console.log('[Configurator] Exact match found - variant:', option.value, 'options:', variantValues.slice(0, selectedOptions.length));
        return option;
      }
    }

    console.log('[Configurator] No exact match, trying partial match. Changed index:', changedIndex);

    // Pass 2: best partial match anchored on the changed option
    if (changedIndex !== undefined) {
      let bestVariant = null;
      let bestScore = -1;
      for (const option of allVariantOptions) {
        const s = scoreVariant(getVariantValues(option), selectedOptions, changedIndex);
        if (s > bestScore) {
          bestScore = s;
          bestVariant = option;
        }
      }
      if (bestVariant) {
        console.log('[Configurator] Partial match (anchored) - variant:', bestVariant.value, 'score:', bestScore);
        return bestVariant;
      }
    }

    // Pass 3: best match without anchor (changed value may not exist in any variant)
    let bestVariant = null;
    let bestScore = -1;
    for (const option of allVariantOptions) {
      const s = scoreVariant(getVariantValues(option), selectedOptions, undefined);
      if (s > bestScore) {
        bestScore = s;
        bestVariant = option;
      }
    }

    if (bestVariant) {
      console.log('[Configurator] Partial match (unanchored) - variant:', bestVariant.value, 'score:', bestScore);
      return bestVariant;
    }

    console.log('[Configurator] No match found at all.');
    return null;
  }

  /**
   * Update UI elements based on selected variant
   */
  function updateVariantUI(card, form, variantOption) {
    const price = variantOption.dataset.price;
    const comparePrice = variantOption.dataset.comparePrice;
    const available = variantOption.dataset.available === 'true';
    const imageUrl = variantOption.dataset.image;

    console.log('[Configurator] updateVariantUI called with price:', price, 'comparePrice:', comparePrice);

    // Update ALL price-current elements in the card (both grid and list views)
    // This ensures both views stay in sync and the visible one always updates
    if (card && price) {
      const allPriceEls = card.querySelectorAll('.price-current');
      console.log('[Configurator] Found', allPriceEls.length, 'price-current elements in card');
      allPriceEls.forEach(function(el) {
        var oldPrice = el.textContent;
        el.textContent = price;
        if (oldPrice !== price) {
          console.log('[Configurator] Price updated from', oldPrice, 'to', price);
          // Brief visual flash to confirm update is visible
          el.style.transition = 'background-color 0.3s';
          el.style.backgroundColor = '#fffde7';
          setTimeout(function() { el.style.backgroundColor = ''; }, 600);
        }
      });
      // Defensive re-apply after delays (guards against external scripts reverting)
      setTimeout(function() {
        allPriceEls.forEach(function(el) { if (el.textContent !== price) el.textContent = price; });
      }, 200);
      setTimeout(function() {
        allPriceEls.forEach(function(el) { if (el.textContent !== price) el.textContent = price; });
      }, 600);
    } else {
      console.warn('[Configurator] Could not update price - card:', !!card, 'price:', price);
    }

    // Update compare price in all views
    if (card) {
      var allCompareEls = card.querySelectorAll('.price-compare');
      allCompareEls.forEach(function(el) {
        if (comparePrice) {
          el.textContent = comparePrice;
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
    }

    // Update image if provided (grid view only)
    if (imageUrl) {
      const cardImage = card.querySelector('.card-image img');
      if (cardImage) {
        cardImage.src = imageUrl;
      }
    }

    // Update button state
    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) {
      if (available) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('disabled');
        submitBtn.innerHTML = '<i class="icon-cart" aria-hidden="true"></i> <span class="btn-text">Hinzufügen</span>';
      } else {
        submitBtn.disabled = true;
        submitBtn.classList.add('disabled');
        submitBtn.innerHTML = '<span class="btn-text">Ausverkauft</span>';
      }
    }

    // Update heart button data attributes for projekt
    const heartBtn = form.querySelector('.nf-projekt-btn-add');
    if (heartBtn) {
      heartBtn.dataset.variantId = variantOption.value;
      heartBtn.dataset.productPrice = price || '';
      heartBtn.dataset.comparePrice = comparePrice || '';
      if (imageUrl) {
        heartBtn.dataset.productImage = imageUrl;
      }
      // Update variant title
      const variantTitle = variantOption.textContent.trim();
      if (variantTitle && variantTitle !== 'Default Title') {
        heartBtn.dataset.variantTitle = variantTitle;
      }
    }
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  // Initialize when DOM is ready
  console.log('[Configurator] Document readyState:', document.readyState);

  if (document.readyState === 'loading') {
    console.log('[Configurator] Waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', init);
  } else {
    console.log('[Configurator] DOM already ready, initializing now');
    init();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('#collection-configurator')) {
      init();
    }
  });

  // Expose resolveVariantFromDropdowns globally for nf-projekt.js
  window.configuratorResolveVariant = resolveVariantFromDropdowns;

})();
